import React, { useEffect, useState } from "react";
import * as THREE from "three";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../ui/Collapsible";
import { eulerDegreesToQuaternion, quaternionToEulerDegrees } from "../../core/transform";
import { useSceneStore } from "../../stores/sceneStore";
import { logger } from "../../utils/logger";
import { commitBodyTransform } from "../../viewer/utils/commitTransform";
import InspectorPhysicsLockedCollapsibleWrap from "./InspectorPhysicsLockedCollapsibleWrap";
import OffsetEditor from "./OffsetEditor";
import TransformGizmoHeaderActions from "./TransformGizmoHeaderActions";

const BodyTransformSection = React.memo(function BodyTransformSection({
  selectedBodyUuid,
  robotActions,
  isSimRunning,
  physicsAuthoringLocked,
  transformEditorBlock,
  showPositionGizmo,
  showRotationGizmo,
  onTogglePositionGizmo,
  onToggleRotationGizmo,
}) {
  const [localPosition, setLocalPosition] = useState([0, 0, 0]);
  const [localRotation, setLocalRotation] = useState([0, 0, 0]);
  const [globalPosition, setGlobalPosition] = useState([0, 0, 0]);

  const calculateGlobalPosition = (targetUuid) => {
    try {
      let sceneObj = null;
      if (
        window.sceneObjectsRef &&
        window.sceneObjectsRef.current &&
        window.sceneObjectsRef.current[targetUuid]
      ) {
        sceneObj = window.sceneObjectsRef.current[targetUuid];
      }

      if (sceneObj) {
        const chain = [];
        const seen = new Set();
        let cur = sceneObj;
        while (cur) {
          if (seen.has(cur)) {
            throw new RangeError("Detected cyclic parent chain while computing world position");
          }
          seen.add(cur);
          chain.push(cur);
          cur = cur.parent;
        }
        const world = new THREE.Matrix4().identity();
        for (let i = chain.length - 1; i >= 0; i -= 1) {
          const node = chain[i];
          if (node && typeof node.updateMatrix === "function") node.updateMatrix();
          if (node && node.matrix) world.multiply(node.matrix);
        }
        const worldPosition = new THREE.Vector3();
        worldPosition.setFromMatrixPosition(world);
        return [worldPosition.x, worldPosition.y, worldPosition.z];
      }
    } catch (error) {
      logger.warn("[BodyPropertiesPanel] Could not calculate global position:", error);
    }
    return [0, 0, 0];
  };

  useEffect(() => {
    if (!selectedBodyUuid) return;
    let displayPosition;
    let displayRotation;
    if (isSimRunning) {
      try {
        let sceneObj = null;
        if (
          window.sceneObjectsRef &&
          window.sceneObjectsRef.current &&
          window.sceneObjectsRef.current[selectedBodyUuid]
        ) {
          sceneObj = window.sceneObjectsRef.current[selectedBodyUuid];
        }
        if (sceneObj) {
          displayPosition = [sceneObj.position.x, sceneObj.position.y, sceneObj.position.z];
          const euler = new THREE.Euler();
          euler.setFromQuaternion(sceneObj.quaternion);
          displayRotation = [
            THREE.MathUtils.radToDeg(euler.x),
            THREE.MathUtils.radToDeg(euler.y),
            THREE.MathUtils.radToDeg(euler.z),
          ];
        } else {
          displayPosition = robotActions.getBodyPosition(selectedBodyUuid);
          displayRotation = quaternionToEulerDegrees(
            robotActions.getBodyRotation(selectedBodyUuid)
          );
        }
      } catch {
        displayPosition = robotActions.getBodyPosition(selectedBodyUuid);
        displayRotation = quaternionToEulerDegrees(robotActions.getBodyRotation(selectedBodyUuid));
      }
    } else {
      displayPosition = robotActions.getBodyPosition(selectedBodyUuid);
      displayRotation = quaternionToEulerDegrees(robotActions.getBodyRotation(selectedBodyUuid));
    }
    setLocalPosition(Array.isArray(displayPosition) ? [...displayPosition] : [0, 0, 0]);
    setLocalRotation(Array.isArray(displayRotation) ? [...displayRotation] : [0, 0, 0]);
    setGlobalPosition(calculateGlobalPosition(selectedBodyUuid));
  }, [selectedBodyUuid, isSimRunning, robotActions]);

  useEffect(() => {
    if (!selectedBodyUuid) return;
    const unsubscribe = useSceneStore.subscribe(() => {
      if (!selectedBodyUuid) return;
      const robotPosition = robotActions.getBodyPosition(selectedBodyUuid);
      const storeRotation = robotActions.getBodyRotation(selectedBodyUuid);
      setLocalPosition(Array.isArray(robotPosition) ? [...robotPosition] : [0, 0, 0]);
      const eulerDegrees = quaternionToEulerDegrees(storeRotation);
      setLocalRotation(Array.isArray(eulerDegrees) ? [...eulerDegrees] : [0, 0, 0]);
      setGlobalPosition(calculateGlobalPosition(selectedBodyUuid));
    });
    return unsubscribe;
  }, [selectedBodyUuid, robotActions]);

  const handlePositionChange = (axis, value) => {
    if (!selectedBodyUuid) return;
    const currentPos = robotActions.getBodyPosition(selectedBodyUuid) || [0, 0, 0];
    const newPos = [...currentPos];
    newPos[axis] = value;
    const currentQuat = robotActions.getBodyRotation(selectedBodyUuid) || [1, 0, 0, 0];
    commitBodyTransform(robotActions, selectedBodyUuid, newPos, currentQuat);
  };

  const handleGlobalPositionChange = (axis, value) => {
    if (!selectedBodyUuid) return;
    const nextGlobal = [...globalPosition];
    nextGlobal[axis] = value;
    let nextLocal = null;
    try {
      let sceneObj = null;
      if (
        window.sceneObjectsRef &&
        window.sceneObjectsRef.current &&
        window.sceneObjectsRef.current[selectedBodyUuid]
      ) {
        sceneObj = window.sceneObjectsRef.current[selectedBodyUuid];
      }
      if (sceneObj) {
        const chain = [];
        const seen = new Set();
        let cur = sceneObj;
        while (cur) {
          if (seen.has(cur)) {
            throw new RangeError("Detected cyclic parent chain while computing local position");
          }
          seen.add(cur);
          chain.push(cur);
          cur = cur.parent;
        }
        const parentWorld = new THREE.Matrix4().identity();
        for (let i = chain.length - 1; i >= 1; i -= 1) {
          const node = chain[i];
          if (node && typeof node.updateMatrix === "function") node.updateMatrix();
          if (node && node.matrix) parentWorld.multiply(node.matrix);
        }
        const invParentWorld = parentWorld.clone();
        invParentWorld.invert();
        const worldPos = new THREE.Vector3(nextGlobal[0], nextGlobal[1], nextGlobal[2]);
        const localPos = worldPos.applyMatrix4(invParentWorld);
        nextLocal = [localPos.x, localPos.y, localPos.z];
      }
    } catch (error) {
      logger.warn("[BodyPropertiesPanel] Could not compute local position from global:", error);
    }

    if (!nextLocal) nextLocal = [...nextGlobal];
    const currentQuat = robotActions.getBodyRotation(selectedBodyUuid) || [1, 0, 0, 0];
    commitBodyTransform(robotActions, selectedBodyUuid, nextLocal, currentQuat);
  };

  const handleRotationChange = (axis, value) => {
    if (!selectedBodyUuid) return;
    const currentRotationDeg = quaternionToEulerDegrees(
      robotActions.getBodyRotation(selectedBodyUuid)
    );
    currentRotationDeg[axis] = value;
    const newQuat = eulerDegreesToQuaternion(currentRotationDeg);
    const currentPos = robotActions.getBodyPosition(selectedBodyUuid) || [0, 0, 0];
    commitBodyTransform(robotActions, selectedBodyUuid, currentPos, newQuat);
  };

  const handleQuaternionChange = (idx, value) => {
    if (!selectedBodyUuid) return;
    const currentPos = robotActions.getBodyPosition(selectedBodyUuid) || [0, 0, 0];
    const currentQuat = robotActions.getBodyRotation(selectedBodyUuid) || [1, 0, 0, 0];
    const nextQuat = [...currentQuat];
    nextQuat[idx] = value;
    commitBodyTransform(robotActions, selectedBodyUuid, currentPos, nextQuat);
  };

  return (
    <InspectorPhysicsLockedCollapsibleWrap locked={physicsAuthoringLocked}>
      <Collapsible defaultOpen>
        <CollapsibleTrigger
          quiet
          aria-label="Toggle Transform"
          actions={
            <TransformGizmoHeaderActions
              disabled={transformEditorBlock.disabled}
              disabledTitle={transformEditorBlock.title}
              showPositionGizmo={showPositionGizmo}
              showRotationGizmo={showRotationGizmo}
              onTogglePositionGizmo={onTogglePositionGizmo}
              onToggleRotationGizmo={onToggleRotationGizmo}
            />
          }
        >
          <span className="min-w-0 truncate text-left">Transform</span>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <OffsetEditor
            embedded
            globalPosition={globalPosition}
            localPosition={localPosition}
            localRotationDeg={localRotation}
            quaternion={robotActions.getBodyRotation(selectedBodyUuid) || [1, 0, 0, 0]}
            onGlobalPositionChange={handleGlobalPositionChange}
            onPositionChange={handlePositionChange}
            onRotationChange={handleRotationChange}
            onQuaternionChange={handleQuaternionChange}
            disabled={transformEditorBlock.disabled}
            disabledTitle={transformEditorBlock.title}
          />
        </CollapsibleContent>
      </Collapsible>
    </InspectorPhysicsLockedCollapsibleWrap>
  );
});

export default BodyTransformSection;
